import Timetable from 'src/api/timetable.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

const schoolYearMixin = {
  data: () => ({
    schoolYears: [],
    toggleDatePickers: {
      begins_at: false,
      ends_at: false
    }
  }),

  methods: {
    async fetchSchoolYears () {
      if (!this.getSchool) return

      if (this.getSchool.timetable?.id === this.school?.timetable?.id) {
        this.schoolYears = this.school?.timetable.schoolyears
        return
      }

      if (this.getSchool.timetable?.id === this.$me?.school.timetable?.id) {
        this.schoolYears = this.$me.school.timetable.schoolyears
        return
      }

      const { data } = await Timetable.getTimetableSchoolYears(this.getSchool.timetable.id)
      this.schoolYears = data.data
    },
    getDateRangeBySchoolYear (beginsAt, endsAt) {
      // returns max & min dates based on current school year
      if (!beginsAt || !endsAt) return
      // fallback when dates are corrupted
      const currentYear = this.$moment().year()
      const isTodayAfterYearChange = this.$moment().isAfter(`${currentYear}-06-20 00:00:00`)
      const beginsAtFallback = isTodayAfterYearChange ? this.$moment() : this.$moment().subtract(1, 'years')
      const endsAtFallback = isTodayAfterYearChange ? this.$moment().add(1, 'years') : this.$moment()

      const isBeginsAtValid = this.$moment(beginsAt).isValid()
      const localBeginsAt = isBeginsAtValid ? this.$moment(beginsAt) : beginsAtFallback

      const isEndsAtValid = this.$moment(endsAt).isValid()
      const localEndsAt = isEndsAtValid ? this.$moment(endsAt) : endsAtFallback

      const date = {
        // max
        maxDateBeginsAt: localBeginsAt.clone().endOf('year'),
        maxDateEndsAt: localEndsAt.clone().endOf('year'),
        // min
        minDateBeginsAt: localBeginsAt.clone().startOf('year'),
        minDateEndsAt: localEndsAt.clone().startOf('year')
      }
      return {
        // max
        maxDateBeginsAt: this.$dateFormat.timestamp(date.maxDateBeginsAt),
        maxDateEndsAt: this.$dateFormat.timestamp(date.maxDateEndsAt),
        // min
        minDateBeginsAt: this.$dateFormat.timestamp(date.minDateBeginsAt),
        minDateEndsAt: this.$dateFormat.timestamp(date.minDateEndsAt)
      }
    }
  },

  computed: {
    dateRangeBySuggestedSchoolYear () {
      const dateRange = {
        minDateBeginsAt: this.$dateFormat.timestamp(this.$moment(this.suggestSchoolYearRangePrototype.begins_at).startOf('year')),
        maxDateBeginsAt: this.$dateFormat.timestamp(this.$moment(this.suggestSchoolYearRangePrototype.begins_at).endOf('year')),
        minDateEndsAt: this.$dateFormat.timestamp(this.$moment(this.suggestSchoolYearRangePrototype.ends_at).startOf('year')),
        maxDateEndsAt: this.$dateFormat.timestamp(this.$moment(this.suggestSchoolYearRangePrototype.ends_at).endOf('year'))
      }
      // onboarding
      // TODO fix onboarding dateRange
      // if (!this.currentSchoolYear) return this.dateRangeOnboarding
      return dateRange
    },
    dateRangeOnboarding () {
      return {
        minDateBeginsAt: this.$dateFormat.timestamp(this.$moment().startOf('year').subtract(1, 'years')),
        maxDateBeginsAt: this.$dateFormat.timestamp(this.$moment().endOf('year')),
        minDateEndsAt: this.$dateFormat.timestamp(this.$moment().startOf('year')),
        maxDateEndsAt: this.$dateFormat.timestamp(this.$moment().endOf('year').add(1, 'years'))
      }
    },
    suggestSchoolYearRangePrototype () {
      // TODO/NOTE: We are using "GlobalPrototypes.moment()" here instead of "this.$moment()" because the global prototype
      // might not be available at time of execution. This is mainly because of direct imports of
      // "schoolYearMixin.computed.suggestSchoolYearRangePrototype()" in other files. This should be refactored.
      const currentYear = GlobalPrototypes.moment().year()
      const isTodayAfterYearChange = GlobalPrototypes.moment().isAfter(`${currentYear}-06-20 00:00:00`)

      const beginsAtYear = isTodayAfterYearChange ? currentYear : currentYear - 1
      const endsAtYear = isTodayAfterYearChange ? currentYear + 1 : currentYear

      return {
        key: `${beginsAtYear}-${endsAtYear}`,
        begins_at: `${beginsAtYear}-08-01`,
        ends_at: `${endsAtYear}-07-15`,
        name: `${beginsAtYear} / ${endsAtYear}`
      }
    },

    upcomingSchoolYear () {
      // selected first schoolyear thats bigger than the current
      return this.schoolYears.find(year => year.key > this.currentSchoolYear.key)
    },

    isEndOfSchoolYear () {
      if (!this.currentSchoolYear) return false
      const today = this.$moment()
      const schoolYearEnd = this.$moment(this.currentSchoolYear.ends_at)
      return today.isAfter(schoolYearEnd.subtract(3, 'weeks'))
    },

    isLast10WeeksOfSchoolYear () {
      if (!this.currentSchoolYear) return false
      const today = this.$moment()
      const schoolYearEnd = this.$moment(this.currentSchoolYear.ends_at)
      return today.isAfter(schoolYearEnd.subtract(10, 'weeks'))
    },

    isStartOfSchoolYear () {
      if (!this.currentSchoolYear) return false
      const today = this.$moment()
      const schoolYearStart = this.$moment(this.currentSchoolYear.begins_at)
      return today.isBefore(schoolYearStart.add(3, 'weeks'))
    },

    // 8 weeks equals 60 days
    is8WeeksAfterSchoolYearChange () {
      if (!this.currentSchoolYear) return false
      const today = this.$moment()
      const schoolYearStart = this.$moment(this.currentSchoolYear.begins_at)
      return today.isBefore(schoolYearStart.add(8, 'weeks'))
    },

    hasMoreThanOneSchoolYear () {
      if (!this.currentSchoolYear) return false
      return this.getSchool?.timetable.schoolyears.length > 1
    },

    getSchool () {
      if (this.school && Object.keys(this.school).length) return this.school
      return this.$me?.school
    },
    currentSchoolYear () { return this.schoolYears?.find(el => el.id === this.activeSchoolYearId) },
    activeSchoolYearId () { return this.getSchool?.timetable.active_schoolyear_id }
  },

  watch: {
    getSchool: {
      handler: async function (newVal, oldVal) {
        if (!newVal || this.$compare(newVal, oldVal)) return
        await this.fetchSchoolYears()
      },
      immediate: true
    }
  },
  created () {},
  mounted () {}
}

export default schoolYearMixin
